import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '../../contexts/ThemeContext';
import { useAuth } from '../../contexts/AuthContext';
import SharedHeader from './SharedHeader';
import { getClaimRequests, updateClaimRequest, createCustomerTransaction } from '../../services/api';
import { toast } from 'react-hot-toast';

// Keep conversion in sync with dashboard
const computePointsForAmount = (amt) => {
  const perRupee = 0.1; // fallback conversion, can be made configurable
  const n = Number(amt) || 0;
  return Math.max(0, Math.floor(n * perRupee));
};

const ClaimRequestsPage = () => {
  const { currentUser } = useAuth();
  const { theme } = useTheme();
  const navigate = useNavigate();

  const [claimRequests, setClaimRequests] = useState([]);
  const [claimLoading, setClaimLoading] = useState(false);
  const [showApproveModal, setShowApproveModal] = useState(false);
  const [approveTarget, setApproveTarget] = useState(null);
  const [approveAmount, setApproveAmount] = useState('');

  // Filters, sorting, paging
  const [searchTerm, setSearchTerm] = useState(''); // general search
  const [nameEmail, setNameEmail] = useState('');   // specific name/email
  const [amountMin, setAmountMin] = useState('');
  const [amountMax, setAmountMax] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [sortBy, setSortBy] = useState('requestDate'); // 'customerName' | 'customerEmail' | 'amount' | 'requestDate' | 'status'
  const [sortDir, setSortDir] = useState('desc'); // 'asc' | 'desc'
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [datePreset, setDatePreset] = useState(''); // '', 'today', 'lastWeek', 'lastMonth', 'custom'

  // Helpers for date presets and input values
  const toDateInputValue = (iso) => {
    if (!iso) return '';
    const d = new Date(iso);
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const updateDatePreset = (preset) => {
    setDatePreset(preset);
    const now = new Date();
    const end = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 23, 59, 59, 999);
    let start;
    if (preset === 'today') {
      start = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 0, 0, 0, 0);
    } else if (preset === 'lastWeek') {
      // Last 7 days including today
      start = new Date(now.getFullYear(), now.getMonth(), now.getDate() - 6, 0, 0, 0, 0);
    } else if (preset === 'lastMonth') {
      // Previous calendar month
      const firstOfThisMonth = new Date(now.getFullYear(), now.getMonth(), 1, 0, 0, 0, 0);
      const lastOfLastMonth = new Date(firstOfThisMonth - 1);
      start = new Date(lastOfLastMonth.getFullYear(), lastOfLastMonth.getMonth(), 1, 0, 0, 0, 0);
      const endOfLastMonth = new Date(lastOfLastMonth.getFullYear(), lastOfLastMonth.getMonth(), lastOfLastMonth.getDate(), 23, 59, 59, 999);
      setDateFrom(start.toISOString());
      setDateTo(endOfLastMonth.toISOString());
      return;
    } else if (preset === 'custom') {
      // Keep current custom values; do not override
      return;
    } else {
      // All time
      setDateFrom('');
      setDateTo('');
      return;
    }
    setDateFrom(start.toISOString());
    setDateTo(end.toISOString());
  };

  const fetchClaims = async () => {
    try {
      setClaimLoading(true);
      const myEmail = (currentUser?.email || '').toLowerCase();
      const claimsResp = await getClaimRequests({});
      const list = Array.isArray(claimsResp.extractedData)
        ? claimsResp.extractedData
        : (claimsResp.extractedData?.records || []);
      const mine = (list || [])
        .filter(c => (c.businessEmail || c.email || '').toLowerCase() === myEmail)
        .map((it, idx) => ({
          id: it._id || it.id || String(idx),
          customerEmail: it.customerEmail || it.email_id || '',
          customerName: it.customerName || it.name || '',
          businessEmail: it.businessEmail || it.email || myEmail,
          businessName: it.businessName || '',
          amount: Number(it.amount || 0),
          billImage: it.billImage || '',
          status: (it.status || 'pending').toLowerCase(),
          requestDate: it.requestDate || it.created_on || new Date().toISOString(),
        }));
      setClaimRequests(mine);
    } catch (e) {
      console.warn('Claim requests fetch failed', e?.message || e);
    } finally {
      setClaimLoading(false);
    }
  };

  useEffect(() => {
    fetchClaims();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUser?.email]);

  // Reset to first page when filters change
  useEffect(() => {
    setPage(1);
  }, [searchTerm, nameEmail, amountMin, amountMax, dateFrom, dateTo, datePreset]);

  // Derived: filtered, sorted, paginated rows
  const normalized = (v) => (v == null ? '' : String(v)).toLowerCase();
  const withinDateRange = (iso) => {
    const d = new Date(iso);
    if (dateFrom) {
      const f = new Date(dateFrom);
      if (d < f) return false;
    }
    if (dateTo) {
      const t = new Date(dateTo);
      // include end date full day
      t.setHours(23,59,59,999);
      if (d > t) return false;
    }
    return true;
  };

  const filtered = claimRequests.filter((r) => {
    if (!withinDateRange(r.requestDate)) return false;
    // Name/Email specific filter
    if (nameEmail) {
      const qn = normalized(nameEmail);
      const matchNE = normalized(r.customerName).includes(qn) || normalized(r.customerEmail).includes(qn);
      if (!matchNE) return false;
    }
    // Amount range filter
    const amt = Number(r.amount || 0);
    if (amountMin !== '' && amt < Number(amountMin)) return false;
    if (amountMax !== '' && amt > Number(amountMax)) return false;
    // Generic search (optional)
    if (searchTerm) {
      const q = normalized(searchTerm);
      const match = (
        normalized(r.customerName).includes(q) ||
        normalized(r.customerEmail).includes(q) ||
        String(r.amount || '').toLowerCase().includes(q) ||
        normalized(r.status).includes(q)
      );
      if (!match) return false;
    }
    return true;
  });

  const sorted = [...filtered].sort((a, b) => {
    const dir = sortDir === 'asc' ? 1 : -1;
    const getVal = (r) => {
      switch (sortBy) {
        case 'customerName': return normalized(r.customerName);
        case 'customerEmail': return normalized(r.customerEmail);
        case 'amount': return Number(r.amount || 0);
        case 'status': return normalized(r.status);
        case 'requestDate': default: return new Date(r.requestDate).getTime();
      }
    };
    const av = getVal(a), bv = getVal(b);
    if (av < bv) return -1 * dir;
    if (av > bv) return 1 * dir;
    return 0;
  });

  const totalPages = Math.max(1, Math.ceil(sorted.length / pageSize));
  const currentPage = Math.min(page, totalPages);
  const start = (currentPage - 1) * pageSize;
  const rows = sorted.slice(start, start + pageSize);

  const toggleSort = (key) => {
    if (sortBy === key) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortBy(key);
      setSortDir('asc');
    }
  };

  const approveClaimWithAmount = async () => {
    if (!approveTarget) return;
    try {
      const amt = Number(approveAmount) || 0;
      if (amt <= 0) {
        toast.error('Enter a valid amount');
        return;
      }
      await updateClaimRequest(approveTarget.id, {
        status: 'approved',
        amount: amt,
        approvedDate: new Date().toISOString(),
      });

      const points = computePointsForAmount(amt);
      await createCustomerTransaction({
        email_id: approveTarget.customerEmail,
        name: approveTarget.customerName || (approveTarget.customerEmail?.split('@')[0] || 'Customer'),
        amount: amt,
        point_given: points,
        transaction_date: new Date().toISOString(),
        payment_method: 'UPI',
        status: 'Completed',
      });

      toast.success(`Approved claim for ₹${amt.toLocaleString()} and credited ${points} pts`);
      setShowApproveModal(false);
      setApproveTarget(null);
      setApproveAmount('');
      await fetchClaims();
    } catch (e) {
      console.error('Approve claim (modal) failed', e);
      toast.error('Failed to approve claim');
    }
  };

  const handleRejectClaim = async (req) => {
    try {
      await updateClaimRequest(req.id, { status: 'rejected' });
      toast.success('Claim rejected');
      setClaimRequests(prev => prev.map(x => x.id === req.id ? { ...x, status: 'rejected' } : x));
    } catch (e) {
      console.error('Reject claim failed', e);
      toast.error('Failed to reject claim');
    }
  };

  const handleMenuAction = (action) => {
    switch (action) {
      case 'manageOffers':
        navigate('/business/offers');
        break;
      case 'salesHistory':
        navigate('/business/sales');
        break;
      case 'profileUpdate':
        navigate('/business/profile');
        break;
      case 'shopDetails':
        navigate('/business/profile');
        break;
      case 'buyPoints':
        // Could navigate to a buy points page if exists
        navigate('/business/dashboard');
        break;
      case 'claimRequests':
        // Already here
        break;
      default:
        break;
    }
  };
  const handleBack = () => {
    navigate('/business/dashboard');
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <SharedHeader
        title={currentUser?.displayName || 'My Business'}
        subtitle="Claim Requests"
        onMenuAction={handleMenuAction}
      />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="bg-white rounded-xl shadow-md p-6">
        <div className="flex items-center gap-4 mb-6">
          <button
            onClick={handleBack}
            className="text-indigo-600 hover:underline font-medium"
          >
            ← Back
          </button>
        </div>
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-xl font-bold text-gray-900">Claim Point Requests</h3>
            {claimLoading && (
              <span className="text-sm text-gray-500">Loading...</span>
            )}
          </div>

          {/* Filters */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Search (name, email, amount)</label>
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Type to search..."
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
              />
            </div>
           
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Date Range</label>
              <select
                value={datePreset}
                onChange={(e) => updateDatePreset(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
              >
                <option value="">All time</option>
                <option value="today">Today</option>
                <option value="lastWeek">Last Week</option>
                <option value="lastMonth">Last Month</option>
                <option value="custom">Custom</option>
              </select>
            </div>
            {datePreset === 'custom' && (
              <>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">From</label>
                  <input
                    type="date"
                    value={toDateInputValue(dateFrom)}
                    onChange={(e) => setDateFrom(e.target.value ? new Date(e.target.value).toISOString() : '')}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">To</label>
                  <input
                    type="date"
                    value={toDateInputValue(dateTo)}
                    onChange={(e) => setDateTo(e.target.value ? new Date(e.target.value).toISOString() : '')}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                  />
                </div>
              </>
            )}
            <div className="flex items-end">
              <button
                onClick={() => { setSearchTerm(''); setNameEmail(''); setAmountMin(''); setAmountMax(''); setDateFrom(''); setDateTo(''); setDatePreset(''); }}
                className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
              >
                Clear Filters
              </button>
            </div>
          </div>

          {claimRequests.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th onClick={() => toggleSort('customerName')} className="text-left py-3 px-4 font-semibold text-gray-700 cursor-pointer">Customer {sortBy==='customerName' ? (sortDir==='asc'?'▲':'▼') : ''}</th>
                    <th onClick={() => toggleSort('customerEmail')} className="text-left py-3 px-4 font-semibold text-gray-700 cursor-pointer">Email {sortBy==='customerEmail' ? (sortDir==='asc'?'▲':'▼') : ''}</th>
                    <th onClick={() => toggleSort('amount')} className="text-right py-3 px-4 font-semibold text-gray-700 cursor-pointer">Amount {sortBy==='amount' ? (sortDir==='asc'?'▲':'▼') : ''}</th>
                    <th onClick={() => toggleSort('requestDate')} className="text-left py-3 px-4 font-semibold text-gray-700 cursor-pointer">Date {sortBy==='requestDate' ? (sortDir==='asc'?'▲':'▼') : ''}</th>
                    <th onClick={() => toggleSort('status')} className="text-left py-3 px-4 font-semibold text-gray-700 cursor-pointer">Status {sortBy==='status' ? (sortDir==='asc'?'▲':'▼') : ''}</th>
                    <th className="text-left py-3 px-4 font-semibold text-gray-700">Bill</th>
                    <th className="text-left py-3 px-4 font-semibold text-gray-700">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {rows.map((req) => (
                    <tr key={req.id} className="hover:bg-gray-50">
                      <td className="py-3 px-4">
                        <div className="font-medium text-gray-900">{req.customerName || 'Customer'}</div>
                      </td>
                      <td className="py-3 px-4 text-gray-700">{req.customerEmail}</td>
                      <td className="py-3 px-4 text-right text-purple-600 font-semibold">₹{Number(req.amount || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                      <td className="py-3 px-4 text-gray-600">{new Date(req.requestDate).toLocaleDateString()}</td>
                      <td className="py-3 px-4">
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${req.status === 'approved' ? 'bg-green-100 text-green-800' : req.status === 'rejected' ? 'bg-red-100 text-red-800' : 'bg-yellow-100 text-yellow-800'}`}>
                          {req.status}
                        </span>
                      </td>
                      <td className="py-3 px-4">
                        {req.billImage ? (
                          <a href={req.billImage} target="_blank" rel="noreferrer" className="text-blue-600 hover:underline">View</a>
                        ) : (
                          <span className="text-gray-400">—</span>
                        )}
                      </td>
                      <td className="py-3 px-4">
                        {req.status === 'pending' ? (
                          <div className="flex items-center gap-2">
                            <button
                              onClick={() => {
                                setApproveTarget(req);
                                setApproveAmount(Number(req.amount || 0));
                                setShowApproveModal(true);
                              }}
                              className="px-3 py-1 rounded-lg text-white bg-green-600 hover:bg-green-700"
                            >
                              Approve
                            </button>
                            <button
                              onClick={() => handleRejectClaim(req)}
                              className="px-3 py-1 rounded-lg text-white bg-red-600 hover:bg-red-700"
                            >
                              Reject
                            </button>
                          </div>
                        ) : (
                          <span className="text-gray-400">—</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="text-center py-8 text-gray-500">
              {claimLoading ? 'Loading claims...' : 'No claim requests found.'}
            </div>
          )}

          {/* Pagination */}
          <div className="flex items-center justify-between mt-4">
            <div className="text-sm text-gray-600">
              Showing {sorted.length ? start + 1 : 0}-{Math.min(start + pageSize, sorted.length)} of {sorted.length}
            </div>
            <div className="flex items-center gap-2">
              <button onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={currentPage === 1} className="px-3 py-1 border rounded disabled:opacity-50">Prev</button>
              {[...Array(totalPages)].map((_, i) => (
                <button key={i} onClick={() => setPage(i + 1)} className={`px-3 py-1 border rounded ${currentPage === i + 1 ? 'bg-indigo-600 text-white' : ''}`}>{i + 1}</button>
              ))}
              <button onClick={() => setPage((p) => Math.min(totalPages, p + 1))} disabled={currentPage === totalPages} className="px-3 py-1 border rounded disabled:opacity-50">Next</button>
            </div>
          </div>
        </div>
      </div>

      {/* Approve Claim Modal */}
      {showApproveModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full overflow-hidden">
            <div className="flex items-center justify-between p-6 border-b border-gray-200">
              <h2 className="text-xl font-bold text-gray-900">Approve Claim</h2>
              <button
                onClick={() => { setShowApproveModal(false); setApproveTarget(null); setApproveAmount(''); }}
                className="text-gray-400 hover:text-gray-600 p-2 rounded-lg hover:bg-gray-100"
              >
                ✕
              </button>
            </div>
            <div className="p-6 space-y-5">
              {approveTarget && (
                <>
                  <div className="text-sm text-gray-700">
                    <div className="font-semibold text-gray-900">Customer</div>
                    <div>{approveTarget.customerName || 'Customer'}</div>
                    <div className="text-gray-500">{approveTarget.customerEmail}</div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Approved Amount (₹)</label>
                    <input
                      type="number"
                      value={approveAmount}
                      onChange={(e) => setApproveAmount(e.target.value)}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      min="0"
                      step="0.01"
                    />
                  </div>
                  <div className="text-sm text-gray-700">
                    <div>Points to credit: <span className="font-semibold text-blue-600">{computePointsForAmount(approveAmount)} pts</span></div>
                  </div>
                  <div className="flex justify-end gap-3 pt-2">
                    <button
                      className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
                      onClick={() => { setShowApproveModal(false); setApproveTarget(null); setApproveAmount(''); }}
                    >
                      Cancel
                    </button>
                    <button
                      className="px-4 py-2 rounded-lg text-white bg-green-600 hover:bg-green-700"
                      onClick={approveClaimWithAmount}
                    >
                      Confirm & Send
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ClaimRequestsPage;
